import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRealtime } from "./index";

/** Subscribes to a SignalR event for the lifetime of the component. */
export function useRealtimeEvent<T>(event: string, handler: (payload: T) => void, enabled = true): void {
  const realtime = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = realtime.on<T>(event, (p) => ref.current(p));
    return unsubscribe;
  }, [realtime, event, enabled]);
}

/** Joins a case group so case-scoped events (agent steps, updates) are received. */
export function useCaseGroup(caseId: string | undefined): void {
  const realtime = useRealtime();
  useEffect(() => {
    if (!caseId) return;
    const group = `case:${caseId}`;
    void realtime.joinGroup(group).catch(() => undefined);
    return () => void realtime.leaveGroup(group);
  }, [realtime, caseId]);
}

/** Invalidates the given query keys whenever one of the events fires. */
export function useInvalidateOn(events: string[], queryKeys: unknown[][]): void {
  const qc = useQueryClient();
  const realtime = useRealtime();
  useEffect(() => {
    const unsubs = events.map((e) => realtime.on(e, () => queryKeys.forEach((k) => void qc.invalidateQueries({ queryKey: k }))));
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realtime, qc, events.join("|"), JSON.stringify(queryKeys)]);
}
